import {useEffect, useState} from 'react';
import {itemsApi} from '@/api/items';

type Resolved = {readonly key: string; readonly url: string | null};

function isPlatformUrl(src: string | undefined): src is string {
  return (
    src !== undefined && /^https?:\/\//i.test(src) && !src.startsWith(globalThis.location.origin)
  );
}

/**
The src to render for a platform image: our stored copy (`/api/images/...`), and never the platform URL
itself. Every fetch from a platform's image CDN tells it which analyst looked at what, and when, so the
browser never goes there: an image we have not stored (not fetched yet, or never stored) is not shown.
`sha256` (an item's stored thumbnail) is used when known; otherwise the copy is looked up by the
platform URL. A `blob:`, `data:` or same-origin src is already ours and passes straight through.

`src` is undefined while the lookup runs and when nothing is stored; `missing` is true only once the
lookup has found nothing.
*/
export function usePlatformImageSrc(
  src: string | undefined,
  sha256: string | null | undefined,
  fetchBySha: (sha256: string) => Promise<Blob> = itemsApi.storedImage,
  fetchBySource: (url: string) => Promise<Blob> = itemsApi.storedImageBySource
): {readonly src: string | undefined; readonly missing: boolean} {
  const lookup = isPlatformUrl(src);
  const key = `${sha256 ?? ''}|${src ?? ''}`;
  const [resolved, setResolved] = useState<Resolved | null>(null);

  useEffect(() => {
    if (!lookup) {
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;
    (sha256 ? fetchBySha(sha256) : fetchBySource(src))
      .then((blob) => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setResolved({key, url: objectUrl});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolved({key, url: null});
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [lookup, key, src, sha256, fetchBySha, fetchBySource]);

  if (!lookup) {
    return {src, missing: false};
  }

  if (resolved?.key !== key) {
    return {src: undefined, missing: false};
  }

  return {src: resolved.url ?? undefined, missing: resolved.url === null};
}
